import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject, takeUntil, filter } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { LucideAngularModule, Calendar, MapPin, Package, Truck, Users, Home, BarChart3, DollarSign, FileText, Settings, Phone, Mail, Target, TrendingUp, PieChart, UserCheck, Clock, CheckSquare, Building, CreditCard, Receipt, ShoppingCart, ChevronDown, ChevronRight } from 'lucide-angular';

interface NavigationItem {
  label: string;
  route?: string;
  icon: any;
  children?: NavigationItem[];
  requiresAuth?: boolean;
}

interface NavigationGroup {
  label: string;
  icon: any;
  children: NavigationItem[];
  expanded?: boolean;
}

@Component({
  selector: 'app-navigation-container',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  template: `
    <div class="relative z-[100]">
      <!-- Desktop/Tablet Navigation Rail -->
      <nav
        *ngIf="!isMobile"
        class="fixed top-[100px] left-5 bottom-5 w-[300px] lg:w-[300px] md:w-[260px] bg-white/95 dark:bg-gray-900/95 backdrop-blur-[20px] border border-gray-200 dark:border-gray-700 rounded-3xl py-4 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden"
        [@slideIn]="isRailVisible ? 'visible' : 'hidden'"
        role="navigation"
        aria-label="Main navigation"
      >
        <div class="h-full flex flex-col relative">
          <!-- Navigation Items -->
          <div class="flex flex-col gap-1 px-4 flex-1 overflow-y-auto">
            <!-- Top Level Items -->
            @for (item of topLevelItems; track item.route) {
              <a
                [routerLink]="item.route"
                class="relative flex items-center px-4 py-3 rounded-xl no-underline text-gray-600 dark:text-gray-300 font-medium text-[14px] transition-all duration-200 ease-out hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:text-brand-600 dark:hover:text-brand-400 focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
                [class]="isActiveRoute(item.route!) ? 'bg-brand-500 text-white font-semibold shadow-lg' : ''"
                [attr.aria-current]="isActiveRoute(item.route!) ? 'page' : null"
                role="menuitem"
              >
                <div class="w-5 h-5 mr-3 flex items-center justify-center">
                  <lucide-icon
                    [img]="item.icon"
                    size="18"
                    class="transition-all duration-200 ease-out"
                  ></lucide-icon>
                </div>
                <span class="flex-1 whitespace-nowrap">{{ item.label }}</span>
              </a>
            }

            <!-- Divider -->
            <div class="h-px bg-gray-200 dark:bg-gray-700 mx-2 my-3"></div>

            <!-- Grouped Navigation Items -->
            @for (group of navigationGroups; track group.label; let groupIndex = $index) {
              <div class="mb-2">
                <!-- Group Header -->
                <button
                  (click)="toggleGroup(groupIndex)"
                  class="w-full flex items-center px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 font-medium text-[13px] transition-all duration-200 ease-out hover:bg-gray-50 dark:hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
                  [class]="isGroupActive(group) ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20' : ''"
                  type="button"
                  [attr.aria-expanded]="group.expanded"
                >
                  <div class="w-5 h-5 mr-3 flex items-center justify-center">
                    <lucide-icon
                      [img]="group.icon"
                      size="16"
                      class="transition-all duration-200 ease-out"
                    ></lucide-icon>
                  </div>
                  <span class="flex-1 text-left whitespace-nowrap">{{ group.label }}</span>
                  <div class="w-4 h-4 ml-2 flex items-center justify-center">
                    <lucide-icon
                      [img]="group.expanded ? ChevronDown : ChevronRight"
                      size="14"
                      class="transition-transform duration-200 ease-out"
                    ></lucide-icon>
                  </div>
                </button>

                <!-- Group Children -->
                <div
                  class="ml-6 space-y-1 overflow-hidden"
                  role="group"
                  [attr.aria-labelledby]="group.label"
                  [@expandCollapse]="group.expanded ? 'expanded' : 'collapsed'"
                >
                    @for (child of group.children; track child.route) {
                      <a
                        [routerLink]="child.route"
                        class="relative flex items-center px-4 py-2.5 rounded-lg no-underline text-gray-600 dark:text-gray-400 font-medium text-[13px] transition-all duration-200 ease-out hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:text-brand-600 dark:hover:text-brand-400 focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
                        [class]="isActiveRoute(child.route!) ? 'bg-brand-500 text-white font-semibold shadow-sm' : ''"
                        [attr.aria-current]="isActiveRoute(child.route!) ? 'page' : null"
                        role="menuitem"
                      >
                        <div class="w-4 h-4 mr-3 flex items-center justify-center">
                          <lucide-icon
                            [img]="child.icon"
                            size="14"
                            class="transition-all duration-200 ease-out"
                          ></lucide-icon>
                        </div>
                        <span class="flex-1 whitespace-nowrap">{{ child.label }}</span>
                        @if (isActiveRoute(child.route!)) {
                          <div class="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-4 bg-white rounded-sm"></div>
                        }
                      </a>
                    }
                </div>
              </div>
            }
          </div>
        </div>
      </nav>

      <!-- Mobile Bottom Navigation -->
      <nav
        *ngIf="isMobile"
        class="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-[20px] border-t border-gray-200 dark:border-gray-700 px-2 py-2 pb-[calc(24px+env(safe-area-inset-bottom))] flex justify-around items-center z-[1000] shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.1)]"
        [@slideUp]="isBottomNavVisible ? 'visible' : 'hidden'"
        role="navigation"
        aria-label="Main navigation"
      >
        @for (item of mobileNavigationItems; track item.route) {
          <a
            [routerLink]="item.route!"
            class="flex flex-col items-center px-2 py-2 rounded-xl no-underline text-gray-500 dark:text-gray-400 font-medium text-[11px] min-w-[56px] flex-1 max-w-[80px] transition-all duration-200 ease-out border-none bg-transparent cursor-pointer hover:bg-brand-50 dark:hover:bg-brand-900/20 hover:text-brand-500 focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
            [class]="isActiveRoute(item.route!) ? 'text-brand-500 font-semibold' : ''"
            [attr.aria-current]="isActiveRoute(item.route!) ? 'page' : null"
            role="menuitem"
          >
            <div
              class="w-8 h-8 rounded-lg flex items-center justify-center mb-1 transition-all duration-200 ease-out"
              [class]="isActiveRoute(item.route!) ? 'bg-brand-500 text-white' : ''"
            >
              <lucide-icon
                [img]="item.icon"
                size="18"
                class="transition-colors duration-200 ease-out"
              ></lucide-icon>
            </div>
            <span class="leading-tight text-center transition-[font-weight] duration-200 ease-out">{{ item.label }}</span>
          </a>
        }
      </nav>
    </div>
  `,
  styles: ``,
  animations: [
    trigger('slideIn', [
      state('hidden', style({ transform: 'translateX(-100%)', opacity: 0 })),
      state('visible', style({ transform: 'translateX(0)', opacity: 1 })),
      transition('hidden <=> visible', animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ]),
    trigger('slideUp', [
      state('hidden', style({ transform: 'translateY(100%)', opacity: 0 })),
      state('visible', style({ transform: 'translateY(0)', opacity: 1 })),
      transition('hidden <=> visible', animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ]),
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1, overflow: 'visible' })),
      transition('collapsed <=> expanded', animate('250ms cubic-bezier(0.4, 0, 0.2, 1)'))
    ])
  ]
})
export class NavigationContainerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  currentRoute = '';
  isMobile = false;
  isRailVisible = true;
  isBottomNavVisible = true;

  // Icon references for template
  ChevronDown = ChevronDown;
  ChevronRight = ChevronRight;

  // Single items (always visible)
  topLevelItems: NavigationItem[] = [
    { label: 'Dashboard', route: '/dashboard', icon: Home }
  ];

  // Grouped navigation items
  navigationGroups: NavigationGroup[] = [
    {
      label: 'Sales & Revenue',
      icon: DollarSign,
      expanded: true,
      children: [
        { label: 'Opportunities', route: '/opportunities', icon: Target },
        { label: 'Quotes', route: '/quotes', icon: FileText },
        { label: 'Orders', route: '/orders', icon: ShoppingCart },
        { label: 'Invoices', route: '/invoices', icon: Receipt },
        { label: 'Payments', route: '/payments', icon: CreditCard }
      ]
    },
    {
      label: 'Customer Management',
      icon: Users,
      expanded: true,
      children: [
        { label: 'Contacts', route: '/contacts', icon: Phone },
        { label: 'Companies', route: '/companies', icon: Building },
        { label: 'Activities', route: '/activities', icon: Clock },
        { label: 'Tasks', route: '/tasks', icon: CheckSquare }
      ]
    },
    {
      label: 'Products & Inventory',
      icon: Package,
      expanded: false,
      children: [
        { label: 'Products', route: '/products', icon: Package },
        { label: 'Suppliers', route: '/suppliers', icon: Truck },
        { label: 'Categories', route: '/categories', icon: FileText }
      ]
    },
    {
      label: 'Analytics & Reports',
      icon: BarChart3,
      expanded: false,
      children: [
        { label: 'Sales Reports', route: '/reports/sales', icon: TrendingUp },
        { label: 'Revenue Analytics', route: '/analytics/revenue', icon: PieChart },
        { label: 'Performance', route: '/analytics/performance', icon: BarChart3 }
      ]
    },
    {
      label: 'System & Users',
      icon: Settings,
      expanded: false,
      children: [
        { label: 'Users', route: '/users', icon: Users },
        { label: 'Roles & Permissions', route: '/roles', icon: UserCheck },
        { label: 'Settings', route: '/settings', icon: Settings }
      ]
    }
  ];

  ngOnInit() {
    // Initialize current route
    this.currentRoute = this.router.url;

    // Track route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
      });

    // Track breakpoint changes
    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isActiveRoute(route: string): boolean {
    return this.currentRoute.startsWith(route);
  }

  toggleGroup(groupIndex: number): void {
    this.navigationGroups[groupIndex].expanded = !this.navigationGroups[groupIndex].expanded;
  }

  isGroupActive(group: NavigationGroup): boolean {
    return group.children.some(child => child.route && this.isActiveRoute(child.route));
  }

  // Get all navigation items for mobile (flattened)
  get mobileNavigationItems(): NavigationItem[] {
    const items = [...this.topLevelItems];
    this.navigationGroups.forEach(group => {
      items.push(...group.children.filter(child => child.route));
    });
    return items.slice(0, 5); // Limit to 5 items for mobile bottom nav
  }
}
